'use client';

import { FC, useEffect, useState } from 'react';

import { AddressDetail } from '@ravago/shared/page-data/models/elements/address.model';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Divider } from '@ravago/shared/radiance/components/divider/components/Divider/Divider';
import { Flyout } from '@ravago/shared/radiance/components/flyout/components/Flyout/Flyout';
import { Spinner } from '@ravago/shared/radiance/components/spinner/components/Spinner/Spinner';
import { Table } from '@ravago/shared/radiance/components/table/components/Table/Table';
import { TableBody } from '@ravago/shared/radiance/components/table/components/TableBody/TableBody';
import { TableCell } from '@ravago/shared/radiance/components/table/components/TableCell/TableCell';
import { TableRow } from '@ravago/shared/radiance/components/table/components/TableRow/TableRow';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';

import { createPortal } from 'react-dom';

import { useTranslations } from '../../../../providers/translation-client-context-provider';

interface Props {
   open: boolean;
   onFlyoutClose: () => void;
   addressId: number | undefined;
   getAddressDetails: (addressId: number) => Promise<AddressDetail | undefined>;
}

const AddressDetailFlyout: FC<Props> = ({ open, onFlyoutClose, addressId, getAddressDetails }: Props) => {
   type AddressDetailDataFetchingState = 'isIdle' | 'isLoading' | 'isError';
   const translate = useTranslations();
   const [addressDetailDataFetchingState, setAddressDetailDataFetchingState] = useState<AddressDetailDataFetchingState>('isLoading');
   const [addressDetail, setAddressDetail] = useState<AddressDetail>();

   useEffect(() => {
      const fetchAddressDetails = async () => {
         if (!addressId) {
            setAddressDetailDataFetchingState('isError');
            return;
         }
         setAddressDetailDataFetchingState('isLoading');
         try {
            const addressDetailResponse = await getAddressDetails(addressId);
            if (!addressDetailResponse) {
               setAddressDetailDataFetchingState('isError');
               return;
            }
            setAddressDetailDataFetchingState('isIdle');
            setAddressDetail(addressDetailResponse);
         } catch (error) {
            setAddressDetailDataFetchingState('isError');
            setAddressDetail(undefined);
         }
      };
      fetchAddressDetails();
   }, [addressId, getAddressDetails]);

   const emptyValue = (value: string | null | 0 | undefined, displayCharacter = '-') => {
      if (value === '') return displayCharacter;
      return value ?? displayCharacter;
   };

   const addressLines = [addressDetail?.line1, addressDetail?.line2, addressDetail?.line3, addressDetail?.line4, addressDetail?.line5]
      .filter(Boolean)
      .map((line, index, arr) => (index !== arr.length - 1 ? `${line}, ` : line));
   const addressDetails = (
      <Box direction="column">
         <Typography component="h4" variant="heading-4" color="strong">
            {addressDetail?.name}
         </Typography>

         <Table rowDividers={false} rounded stroked="horizontal">
            <TableBody>
               <TableRow>
                  <TableCell>
                     <Typography component="span" color="primary" variant="body-highlight">
                        {translate('addresses.address-detail.address')}
                     </Typography>
                  </TableCell>
                  <TableCell>
                     {addressLines}, {addressDetail?.city} {addressDetail?.province} {addressDetail?.postCode} - {addressDetail?.countryName}
                  </TableCell>
               </TableRow>
               <TableRow>
                  <TableCell>
                     <Typography component="span" color="primary" variant="body-highlight">
                        {translate('addresses.address-detail.opening-hours')}
                     </Typography>
                  </TableCell>
                  <TableCell>{emptyValue(addressDetail?.openingHours)}</TableCell>
               </TableRow>
               <TableRow>
                  <TableCell>
                     <Typography component="span" color="primary" variant="body-highlight">
                        {translate('addresses.address-detail.transportation-instructions')}
                     </Typography>
                  </TableCell>
                  <TableCell>{emptyValue(addressDetail?.transportationInstructions)}</TableCell>
               </TableRow>
               <TableRow>
                  <TableCell>
                     <Typography component="span" color="primary" variant="body-highlight">
                        {translate('addresses.address-detail.delivery-instructions')}
                     </Typography>
                  </TableCell>
                  <TableCell>{emptyValue(addressDetail?.deliveryInstructions)}</TableCell>
               </TableRow>
            </TableBody>
         </Table>
      </Box>
   );

   const loadingSpinner = (
      <Box direction="row" justify="center" spacing={{ top: '3xl', bottom: '3xl' }}>
         <Spinner />
      </Box>
   );

   const errorMessage = (
      <Box direction="column" items="center" gap="lg" margin="lg">
         <Typography component="h3" variant="heading-3">
            {translate('addresses.address-detail.no-address-details-found')}
         </Typography>
      </Box>
   );

   const getFlyoutBody = () => {
      if (addressDetailDataFetchingState === 'isLoading') {
         return loadingSpinner;
      }
      if (addressDetailDataFetchingState === 'isError') {
         return errorMessage;
      }
      return addressDetails;
   };

   return (
      <>
         {createPortal(
            <Flyout width="100%" open={open} closeOnOutsideClick onFlyoutClose={onFlyoutClose}>
               <Box
                  direction="column"
                  gap="md"
                  spacing={{ top: 'none', right: 'md', bottom: 'md', left: 'md' }}
                  overflowY="auto"
                  height="calc(100% - 100px)"
               >
                  <Box justify="between" items="center">
                     <Typography component="span" variant="body-highlight" color="strong">
                        {translate('addresses.address')}
                     </Typography>
                     <IconButton icon="times" onClick={onFlyoutClose} />
                  </Box>
                  <Divider />
                  {getFlyoutBody()}
               </Box>
            </Flyout>,
            document.body
         )}
      </>
   );
};

export default AddressDetailFlyout;
